import { useEffect, useState } from 'react';
import { api } from '../api';
import { useAuth } from '../auth.jsx';
import { Badge, Modal, ErrorNote, fmtPKR } from '../components/ui.jsx';

const BADGE = { PENDING: 'half-day', APPROVED: 'active', REJECTED: 'absent', SETTLED: 'administrative' };

export default function Advances() {
  const { can } = useAuth();
  const isHR = can('advances.decide');

  const [advances, setAdvances] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ employeeId: '', amount: '', reason: '' });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => api('/advances').then(setAdvances).catch((e) => setError(e.message));
  useEffect(() => { load(); api('/employees').then(setEmployees); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError('');
    try {
      await api('/advances', { method: 'POST', body: { ...form, amount: Number(form.amount) } });
      setOpen(false);
      setForm({ employeeId: '', amount: '', reason: '' });
      load();
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const decide = async (id, approve) => {
    try {
      await api(`/advances/${id}/decision`, { method: 'POST', body: { approve } });
      load();
    } catch (e) { setError(e.message); }
  };

  return (
    <>
      <div className="filter-bar">
        <div style={{ flex: 1 }} />
        {can('advances.request') && (
          <button className="btn btn-primary" onClick={() => { setOpen(true); setError(''); }}>+ Request Advance</button>
        )}
      </div>
      <ErrorNote error={error} />

      <div className="card emp-table-wrap">
        <table>
          <thead>
            <tr><th>Employee</th><th>Amount</th><th>Repaid</th><th>Reason</th><th>Requested</th><th>Status</th>{isHR && <th></th>}</tr>
          </thead>
          <tbody>
            {advances.map((a) => (
              <tr key={a.id}>
                <td>
                  <div className="emp-name">{a.employee.name}</div>
                  <div className="emp-desg">{a.employee.code} · {a.employee.site.name}</div>
                </td>
                <td className="fw-bold">{fmtPKR(a.amount)}</td>
                <td className="text-muted">{Number(a.amountRepaid) ? fmtPKR(a.amountRepaid) : '—'}</td>
                <td className="text-muted">{a.reason || '—'}</td>
                <td className="text-muted">{new Date(a.requestedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                <td><Badge kind={BADGE[a.status]}>{a.status}</Badge></td>
                {isHR && (
                  <td>
                    {a.status === 'PENDING' && (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button className="btn btn-success btn-sm" onClick={() => decide(a.id, true)}>Approve</button>
                        <button className="btn btn-danger btn-sm" onClick={() => decide(a.id, false)}>Reject</button>
                      </div>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {advances.length === 0 && (
              <tr><td colSpan={7}><div className="empty-state"><p>No advances recorded yet.</p></div></td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title="Request Advance">
        <form onSubmit={submit}>
          <div className="form-group">
            <label>Employee</label>
            <select className="form-select" value={form.employeeId} required
              onChange={(e) => setForm((f) => ({ ...f, employeeId: e.target.value }))}>
              <option value="" disabled>Select employee…</option>
              {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.code} — {emp.name}</option>)}
            </select>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Amount (PKR)</label>
              <input className="form-input" type="number" min="1" value={form.amount} required
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>Reason</label>
              <input className="form-input" value={form.reason}
                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))} placeholder="e.g. Medical emergency" />
            </div>
          </div>
          <ErrorNote error={error} />
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={() => setOpen(false)}>Cancel</button>
            <button className="btn btn-primary" disabled={busy}>{busy ? 'Submitting…' : 'Submit Request'}</button>
          </div>
        </form>
      </Modal>
    </>
  );
}
